// TODO: replace with a generic component once it has been developed
// https://mdtnet.atlassian.net/jira/software/projects/MDT/boards/1?selectedIssue=MDT-1054

import { FC, useMemo } from 'react';

import { SelectChangeEvent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import eCareAlarms from 'api/eCareAlarms/eCareAlarms';
import eCareDictionary from 'api/eCareDictionary/eCareDictionary';
import { useConfirmationDialogContext } from 'components/context/ConfirmationDialogContext/ConfirmationDialogContext';
import InputDropdown from 'components/UI/molecules/InputDropdown/InputDropdown';
import QUERY_KEYS from 'constants/queryKeys/queryKeys';
import sortDictionary from 'helpers/sortDictionary/sortDictionary';
import proECareAlarmMessages from 'translations/specific/pro_ecare_alarm.mjs';

import useStyles from './ECareAlarmInterventionType.styles';

export type Props = {
  alarmId: string;
  interventionTypeId: string | undefined;
  isValid: boolean;
  isClosed: boolean;
};

const ECareAlarmInterventionType: FC<Props> = ({ alarmId, interventionTypeId, isValid, isClosed }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();

  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [QUERY_KEYS.GET_ECARE_ALERT_INTERVENTION_TYPE_STATUS_FOR_PRO, alarmId], [alarmId]);

  const { data, isLoading } = useQuery(queryKey, eCareDictionary.getEcareDictionaryList('INTERVENTION_TYPE'), {
    refetchOnWindowFocus: false,
  });

  const statusMutation = useMutation(eCareAlarms.patchECareAlarm(alarmId), {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([QUERY_KEYS.GET_ECARE_ALARMS, alarmId]);
    },
  });

  const onStatusChange = async ({ target }: SelectChangeEvent<unknown>) => {
    const confirmed = await showConfirmationDialog({
      title: t(proECareAlarmMessages.confirmationDialog.interventionType.confirmationDialogTitle),
      body: t(proECareAlarmMessages.confirmationDialog.confirmationDialogBody),
    });
    if (confirmed) statusMutation.mutate({ intervention_type_id: target.value as number });
  };

  const inputData = useMemo(() => {
    const options = data?.data.map(({ id, name }) => ({
      value: id as number | string,
      label: name as string,
    }));

    return options && sortDictionary(options, 'label');
  }, [data?.data]);

  return (
    <div>
      <Typography className={classes.label} component='label' variant='body2'>
        {t(proECareAlarmMessages.interventionTypeStatus)}
      </Typography>
      {inputData && (
        <InputDropdown
          disabled={isLoading || isClosed}
          error={isValid && !interventionTypeId}
          fullWidth
          options={inputData || undefined}
          required
          value={interventionTypeId}
          onChange={onStatusChange}
        />
      )}
    </div>
  );
};

export default ECareAlarmInterventionType;
